// Omnix/client/ui/VoiceOptionsField.js
import { ComponentBase } from "../core/ComponentBase.js";
import { RpcDataProvider } from "../core/RpcDataProvider.js";
import { z } from "zod";

export class VoiceOptionsField extends ComponentBase {
    constructor(config) {
        super(config);
        this.value = null;
        this.modalForm = null; // This will hold our single, reusable DataForm instance
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'voice-options-field-wrapper';
        }
        const buttonText = this.value && this.value.workflowId ? 'Edit Voice Options' : 'Configure Voice Options';
        this.container.innerHTML = `
            <button type="button" class="btn btn-secondary w-100">
                <i class="bi bi-mic-fill me-2"></i> ${this.t(buttonText)}
            </button>
        `;
        this.container.querySelector('button').addEventListener('click', () => this.showModal());
        return this.container;
    }

    async showModal() {
        // Safety check: if a form is somehow still lingering, destroy it.
        if (this.modalForm) {
            this.modalForm.destroy();
            this.modalForm = null;
        }

        // --- START OF REFACTORED LOGIC ---

        // 1. Define the configuration for the form. This remains the same.
        const formConfig = {
            title: "Voice Campaign Options",
            fields: {
                workflowId: {
                    type: 'reference',
                    label: 'Workflow to Execute on Answer',
                    required: true,
                    reference: {
                        collection: 'workflows',
                        valueField: '_id',
                        displayField: 'name',
                        filter: { tags: 'voice-campaign' }
                    }
                },
                concurrency: { type: 'number', label: 'Simultaneous Calls', default: 10 },
                retries: { type: 'number', label: 'Re-attempts on Failure (0-5)', default: 0 },
                retryInterval: { type: 'number', label: 'Retry Interval (seconds)', default: 60 }
            }
        };

        const modalDataProvider = new RpcDataProvider({
            appClient: this.appClient,
            rpcClass: "WorkflowRpc",
            idField: '_id',
            fields: formConfig.fields
        });
        await modalDataProvider.initialize();

        const voiceOptionsSchema = z.object({
            workflowId: z.string().min(1, "A workflow must be selected."),
            concurrency: z.number().int().min(1).default(10),
            retries: z.number().int().min(0).max(5).default(0),
            retryInterval: z.number().int().min(1).default(60)
        });

        // 2. Create the managed DataForm component instance.
        //    The key is `layout: 'dialog'`, which enables the modal behavior.
        this.modalForm = await this.createManagedComponent("DataForm", null, {
            instanceId: `${this.config.instanceId}-modal-form`,
            layout: 'dialog',
            ...formConfig,
            schema: voiceOptionsSchema,
            dataProvider: modalDataProvider,
            isDetailPane: true, // Prevents auto-loading data
            actions: { save: true, cancel: true }
        });

        // 3. Define handlers to react to the form's events.
        //    These handlers will also be responsible for cleanup.
        const handleSave = (data) => {
            this.setValue(data);
            // Fire a 'change' event so the parent form knows this field's value has updated.
            this.container.dispatchEvent(new Event('change', { bubbles: true }));
            
            this.modalForm.hide(); // Tell the form to hide itself.
            cleanup();
        };

        const handleCancel = () => {
            // The form hides itself automatically before emitting 'cancel'.
            // We just need to handle the cleanup.
            cleanup();
        };

        const cleanup = () => {
            // Unsubscribe from events and destroy the form instance to free up memory.
            this.modalForm.off('dataSaved', handleSave);
            this.modalForm.off('cancel', handleCancel);
            this.modalForm.destroy();
            this.modalForm = null;
        };
        
        // 4. Subscribe to the form's events BEFORE showing it.
        this.modalForm.on('dataSaved', handleSave);
        this.modalForm.on('cancel', handleCancel);
        
        // 5. Set the form's data and call show().
        //    The DataForm component now manages its own DOM attachment and visibility.
        this.modalForm.setData(this.value || {});
        this.modalForm.show();

        // --- END OF REFACTORED LOGIC ---
    }

    getValue() {
        return this.value;
    }

    setValue(data) {
        this.value = data || null;
        this.render();
    }

    destroy() {
        // If the parent component is destroyed, ensure the modal form is also destroyed.
        if (this.modalForm) {
            this.modalForm.destroy();
            this.modalForm = null;
        }
        super.destroy();
    }
}